import { Alert, Box, Button } from "@mui/material";
import { useEffect, useState } from "react";
import { acknowledgeCeleryErrorsByTaskName } from "../api/celtask";
import { acknowledgePluginWorkLogBucket } from "../api/plugin-work-log";
import {
	type CeleryErrorsHealth,
	type CookieMismatchHealth,
	type DbBackupHealth,
	type ExtractionHealth,
	type ExtractionHealthCheck,
	fetchCeleryErrorsHealth,
	fetchCookieMismatchHealth,
	fetchDbBackupHealth,
	fetchEmailQueueHealth,
	fetchExtractionHealth,
	fetchHealthAlertSnoozes,
	fetchLinkedinQueueHealth,
	fetchPluginWorkLogHealth,
	type MemberIdentity,
	type PluginWorkLogHealth,
	type QueueFlowHealth,
	snoozeHealthAlert,
} from "../api/queue-health";
import { formatDurationShort, formatRelativeTimeShort } from "../utils/date-formatting";
import { useGlobalState } from "./globalState";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const formatExtractionFailure = (c: ExtractionHealthCheck): string => {
	const parts: string[] = [];
	if (c.alert_1h) parts.push(`${c.value_1h}/h < ${c.floor_1h}`);
	if (c.alert_24h) parts.push(`${c.value_24h}/24h < ${c.floor_24h}`);
	return `${c.metric}: ${parts.join(", ")}`;
};

const describeMember = (member: MemberIdentity): string =>
	member.name ? `${member.name} (${member.member_id})` : `member ${member.member_id}`;

const formatHours = (hours: number | null): string =>
	hours === null ? "unknown" : formatDurationShort(hours * 3600);

/** Why the queue is unhealthy, in the terms the check actually measured. */
const describeQueueFlow = (q: QueueFlowHealth): string => {
	if (q.stalled) {
		const last = q.last_sent_at
			? `last send ${formatRelativeTimeShort(q.last_sent_at)} ago`
			: "never sent";
		return `${q.backlog} waiting and nothing sent in 24h (${last}).`;
	}
	// Count the rows past the cutoff, not the whole backlog: the rest are
	// legitimately in flight, and Review shows only these.
	const verb = q.stuck === 1 ? "has" : "have";
	return (
		`${q.stuck} of ${q.backlog} waiting ${verb} outlived what throughput ` +
		`explains — oldest queued ${formatRelativeTimeShort(q.oldest_backlog_at)} ago, ` +
		`yet ${q.sent_window} sent in ${q.window_days}d clears the whole backlog ` +
		`in ${formatHours(q.drain_hours)}.`
	);
};

interface SnoozeButtonProps {
	alertKey: string;
	onSnoozed: (alertKey: string) => void;
}

/** Mutes one check for the configured window. Keyed on the check, not the text. */
const SnoozeButton: React.FC<SnoozeButtonProps> = ({ alertKey, onSnoozed }) => (
	<Button
		size="small"
		variant="outlined"
		data-component="health-alert-snooze-button"
		onClick={async () => {
			await snoozeHealthAlert(alertKey).catch(() => null);
			onSnoozed(alertKey);
		}}
		sx={{ ml: 1 }}
	>
		Snooze 24h
	</Button>
);

interface QueueFlowAlertProps {
	label: string;
	health: QueueFlowHealth;
	/** Queues tab, already scoped to this channel. */
	queueHref: string;
	slug: string;
	onSnoozed: (alertKey: string) => void;
}

/** Flow alert for one queue: it has stopped moving, or a row is being skipped. */
const QueueFlowAlert: React.FC<QueueFlowAlertProps> = ({
	label,
	health,
	queueHref,
	slug,
	onSnoozed,
}) =>
	health.alert ? (
		<Alert
			severity={health.stalled ? "error" : "warning"}
			data-component={`${slug}-queue-flow-banner`}
		>
			{label} queue {health.stalled ? "stalled" : "backlog aging"}:{" "}
			{describeQueueFlow(health)} <a href={`${queueHref}&stuck=true`}>Review</a>
			<SnoozeButton
				alertKey={`${slug}-queue:${health.stalled ? "stalled" : "aging"}`}
				onSnoozed={onSnoozed}
			/>
		</Alert>
	) : null;

const formatBackupAge = (ageHours: number | null): string => {
	if (ageHours === null) return "no backup found";
	if (ageHours >= 48) return `${Math.floor(ageHours / 24)}d ago`;
	return `${Math.round(ageHours)}h ago`;
};

export const QueueHealthBanner: React.FC = () => {
	const { currentUser } = useGlobalState();
	const isStaff = !!(currentUser?.is_staff || currentUser?.is_superuser);
	const isAdmin = !!currentUser?.is_superuser;
	const [email, setEmail] = useState<QueueFlowHealth | null>(null);
	const [linkedin, setLinkedin] = useState<QueueFlowHealth | null>(null);
	const [extraction, setExtraction] = useState<ExtractionHealth | null>(null);
	const [celeryErrors, setCeleryErrors] = useState<CeleryErrorsHealth | null>(null);
	const [pluginWorkLog, setPluginWorkLog] = useState<PluginWorkLogHealth | null>(
		null
	);
	const [dbBackup, setDbBackup] = useState<DbBackupHealth | null>(null);
	const [cookieMismatch, setCookieMismatch] = useState<CookieMismatchHealth | null>(
		null
	);
	const [snoozed, setSnoozed] = useState<Set<string>>(new Set());
	const markSnoozed = (alertKey: string) =>
		setSnoozed((prev) => new Set(prev).add(alertKey));

	useEffect(() => {
		if (!isStaff) return;
		let cancelled = false;
		const load = async () => {
			const [e, l, x, c, p, b, m, s] = await Promise.all([
				fetchEmailQueueHealth().catch(() => null),
				fetchLinkedinQueueHealth().catch(() => null),
				fetchExtractionHealth().catch(() => null),
				fetchCeleryErrorsHealth().catch(() => null),
				fetchPluginWorkLogHealth().catch(() => null),
				fetchDbBackupHealth().catch(() => null),
				fetchCookieMismatchHealth().catch(() => null),
				fetchHealthAlertSnoozes().catch(() => null),
			]);
			if (cancelled) return;
			setEmail(e);
			setLinkedin(l);
			setExtraction(x);
			setCeleryErrors(c);
			setPluginWorkLog(p);
			setDbBackup(b);
			setCookieMismatch(m);
			if (s) setSnoozed(new Set(s.snoozed));
		};
		load();
		const id = setInterval(load, POLL_INTERVAL_MS);

		// Refresh the celery-errors list when a user acknowledges a failure on
		// the celery_tasks page or via this banner — the 5-minute poll is too
		// slow to feel responsive.
		const refreshCeleryErrors = async () => {
			const c = await fetchCeleryErrorsHealth().catch(() => null);
			if (!cancelled) setCeleryErrors(c);
		};
		window.addEventListener("celery-errors-changed", refreshCeleryErrors);

		const refreshPluginWorkLog = async () => {
			const p = await fetchPluginWorkLogHealth().catch(() => null);
			if (!cancelled) setPluginWorkLog(p);
		};
		window.addEventListener("plugin-work-log-changed", refreshPluginWorkLog);

		return () => {
			cancelled = true;
			clearInterval(id);
			window.removeEventListener("celery-errors-changed", refreshCeleryErrors);
			window.removeEventListener("plugin-work-log-changed", refreshPluginWorkLog);
		};
	}, [isStaff]);

	const queueKey = (slug: string, h: QueueFlowHealth) =>
		`${slug}-queue:${h.stalled ? "stalled" : "aging"}`;
	const emailUnhealthy = !!email?.alert && !snoozed.has(queueKey("email", email));
	const linkedinUnhealthy =
		!!linkedin?.alert && !snoozed.has(queueKey("linkedin", linkedin));
	const failingChecks = extraction?.checks.filter((c) => c.alert) ?? [];
	const extractionBroken =
		isAdmin && failingChecks.length > 0 && !snoozed.has("extraction");
	const celeryErrorsOver =
		celeryErrors &&
		celeryErrors.unsent > celeryErrors.threshold &&
		!snoozed.has("celery-errors");
	const pluginWorkLogOver =
		pluginWorkLog &&
		(pluginWorkLog.errors > pluginWorkLog.threshold ||
			pluginWorkLog.stuck > pluginWorkLog.threshold) &&
		!snoozed.has("plugin-work-log");
	const dbBackupStale = dbBackup?.alert && !snoozed.has("db-backup");
	const cookieMismatched =
		cookieMismatch && cookieMismatch.count > 0 && !snoozed.has("cookie-mismatch");

	if (
		!emailUnhealthy &&
		!linkedinUnhealthy &&
		!extractionBroken &&
		!celeryErrorsOver &&
		!pluginWorkLogOver &&
		!dbBackupStale &&
		!cookieMismatched
	)
		return null;

	const ackGroup = async (taskName: string) => {
		await acknowledgeCeleryErrorsByTaskName(taskName).catch(() => null);
		window.dispatchEvent(new Event("celery-errors-changed"));
	};

	const ackPluginBucket = async (bucket: "errors" | "stuck") => {
		await acknowledgePluginWorkLogBucket(bucket).catch(() => null);
		window.dispatchEvent(new Event("plugin-work-log-changed"));
	};

	return (
		<>
			{cookieMismatched && (
				<Alert severity="error" data-component="cookie-mismatch-banner">
					<Box sx={{ mb: 0.5 }}>
						LinkedIn account mismatch: {cookieMismatch.count}{" "}
						{cookieMismatch.count === 1 ? "session holds" : "sessions hold"}{" "}
						cookies for a different member than the user is registered with
						— messages sent through{" "}
						{cookieMismatch.count === 1 ? "it" : "them"} go out from the
						wrong account. <a href="/charts/cookies?tab=cookies">Review</a>
						<SnoozeButton
							alertKey="cookie-mismatch"
							onSnoozed={markSnoozed}
						/>
					</Box>
					<Box
						component="ul"
						sx={{ m: 0, pl: 2, listStyle: "disc" }}
						data-component="cookie-mismatch-list"
					>
						{cookieMismatch.mismatches.map((m) => (
							<li key={m.session_id}>
								<Box component="span" sx={{ fontSize: "0.85em" }}>
									<Box component="code" sx={{ fontWeight: 600 }}>
										{m.username}
									</Box>{" "}
									(bot {m.bot_id}) — registered as{" "}
									{describeMember(m.expected)}, cookies belong to{" "}
									{describeMember(m.li_at)}
								</Box>
							</li>
						))}
					</Box>
				</Alert>
			)}
			{email && (
				<QueueFlowAlert
					label="Email"
					slug="email"
					health={email}
					queueHref="/charts/insights?tab=queues&type=email"
					onSnoozed={markSnoozed}
				/>
			)}
			{linkedin && (
				<QueueFlowAlert
					label="LinkedIn"
					slug="linkedin"
					health={linkedin}
					queueHref="/charts/insights?tab=queues&type=linkedin"
					onSnoozed={markSnoozed}
				/>
			)}
			{extractionBroken && (
				<Alert severity="error" data-component="extraction-health-banner">
					Datamining extraction below floor:{" "}
					{failingChecks.map(formatExtractionFailure).join(" · ")}
					<SnoozeButton alertKey="extraction" onSnoozed={markSnoozed} />
				</Alert>
			)}
			{dbBackupStale && dbBackup && (
				<Alert severity="error" data-component="db-backup-health-banner">
					Database backup stale: last backup{" "}
					{formatBackupAge(dbBackup.age_hours)} (threshold{" "}
					{dbBackup.threshold_hours}h).{" "}
					<a href="/charts/db_backups">Review</a>
					<SnoozeButton alertKey="db-backup" onSnoozed={markSnoozed} />
				</Alert>
			)}
			{pluginWorkLogOver && (
				<Alert severity="error" data-component="plugin-work-log-health-banner">
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							flexWrap: "wrap",
						}}
					>
						Chrome plugin (24h):
						<SnoozeButton
							alertKey="plugin-work-log"
							onSnoozed={markSnoozed}
						/>
						{pluginWorkLog.errors > 0 && (
							<>
								<Box component="span">
									{pluginWorkLog.errors} HTTP{" "}
									{pluginWorkLog.errors === 1 ? "error" : "errors"} ·{" "}
									<a href="/charts/plugin_work_log?errors_only=true">
										Review
									</a>
								</Box>
								<Button
									size="small"
									variant="outlined"
									color="inherit"
									onClick={() => ackPluginBucket("errors")}
									sx={{
										fontSize: "0.7rem",
										minWidth: "auto",
										py: 0,
										px: 1,
									}}
									data-component="plugin-work-log-ack-errors-button"
								>
									Ack {pluginWorkLog.errors}
								</Button>
							</>
						)}
						{pluginWorkLog.errors > 0 && pluginWorkLog.stuck > 0 && (
							<Box component="span">·</Box>
						)}
						{pluginWorkLog.stuck > 0 && (
							<>
								<Box component="span">
									{pluginWorkLog.stuck} stuck (no reply 1h+) ·{" "}
									<a href="/charts/plugin_work_log?stuck_only=true">
										Review
									</a>
								</Box>
								<Button
									size="small"
									variant="outlined"
									color="inherit"
									onClick={() => ackPluginBucket("stuck")}
									sx={{
										fontSize: "0.7rem",
										minWidth: "auto",
										py: 0,
										px: 1,
									}}
									data-component="plugin-work-log-ack-stuck-button"
								>
									Ack {pluginWorkLog.stuck}
								</Button>
							</>
						)}
					</Box>
				</Alert>
			)}
			{celeryErrorsOver && (
				<Alert severity="error" data-component="celery-errors-banner">
					<Box sx={{ mb: 0.5 }}>
						Celery: {celeryErrors.unsent} unacknowledged failed{" "}
						{celeryErrors.unsent === 1 ? "task" : "tasks"}.{" "}
						<a href="/charts/celery_tasks?updateTimeInSeconds=10&state=FAILURE">
							Review
						</a>
						<SnoozeButton
							alertKey="celery-errors"
							onSnoozed={markSnoozed}
						/>
					</Box>
					<Box
						component="ul"
						sx={{ m: 0, pl: 2, listStyle: "disc" }}
						data-component="celery-errors-list"
					>
						{celeryErrors.groups.map((g) => (
							<li key={g.task_name || "(unknown)"}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
										flexWrap: "wrap",
									}}
								>
									<Box
										component="code"
										sx={{ fontSize: "0.85em", fontWeight: 600 }}
									>
										{g.task_name || "(unknown task)"}
									</Box>
									<Box
										component="span"
										sx={{ fontSize: "0.85em", opacity: 0.85 }}
									>
										×{g.count} — {g.exception}
									</Box>
									<Button
										size="small"
										variant="outlined"
										color="inherit"
										onClick={() => ackGroup(g.task_name)}
										sx={{
											fontSize: "0.7rem",
											minWidth: "auto",
											py: 0,
											px: 1,
										}}
										data-component="celery-error-ack-group-button"
									>
										Ack {g.count}
									</Button>
								</Box>
							</li>
						))}
					</Box>
				</Alert>
			)}
		</>
	);
};
